/**
 * The item card's subscribers cluster (M5.C4, `specs/M7-C4-subscribers.md` §8).
 *
 * Measured on monday: a **28 × 28** avatar cluster at (1299, 112) inside the 668-wide
 * `item-page-header-component`, with a 95 × 48 name tooltip on hover.
 *
 * **Clicking it opens a manager, which monday's 2026 card no longer has** — deviation D-1.
 * Without a control the `new_post_on_subscribed_item` notification kind is unreachable by a
 * human, and `Item.subscribers`, which monday's own API still returns, would be write-only.
 *
 * The popover is a `<body>` portal, not an absolutely-positioned child: round 4 found that an
 * `overflow:hidden` ancestor silently clips a popover to nothing while `toBeVisible()` still
 * passes, product-wide.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface SubscriberView {
  id: string;
  kind: 'user' | 'team';
  name: string | null;
  email: string | null;
  photoUrl: string | null;
  title: string | null;
}

export interface ItemSubscribersSource {
  list(itemId: string): Promise<SubscriberView[]>;
  isSubscribed(itemId: string): Promise<boolean>;
  subscribe(itemId: string, userIds: string[]): Promise<unknown>;
  unsubscribe(itemId: string, userIds: string[]): Promise<unknown>;
}

export interface ItemSubscribersProps {
  itemId: string;
  meId: string;
  source: ItemSubscribersSource;
  /** How many avatars before the `+n` chip. monday shows a single one; four is our cap. */
  maxAvatars?: number;
}

const initials = (person: SubscriberView): string =>
  (person.name ?? person.email ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

export function ItemSubscribers({ itemId, meId, source, maxAvatars = 4 }: ItemSubscribersProps) {
  const [subscribers, setSubscribers] = useState<SubscriberView[]>([]);
  const [watching, setWatching] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const reload = () => {
    void source.list(itemId).then(setSubscribers).catch(() => undefined);
    void source.isSubscribed(itemId).then(setWatching).catch(() => undefined);
  };

  useEffect(reload, [itemId, source]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const toggleOpen = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ top: rect.bottom + 4, left: Math.max(8, rect.right - 280) });
    setOpen((value) => !value);
  };

  const toggleWatch = async () => {
    setBusy(true);
    try {
      if (watching) await source.unsubscribe(itemId, [meId]);
      else await source.subscribe(itemId, [meId]);
      reload();
    } finally {
      setBusy(false);
    }
  };

  const shown = subscribers.slice(0, maxAvatars);
  const overflow = subscribers.length - shown.length;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="ez-subs-cluster"
        aria-label={`Subscribers (${subscribers.length})`}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="item-subscribers"
        onClick={toggleOpen}
      >
        {shown.length === 0 ? (
          <span className="ez-subs-avatar ez-subs-avatar--empty" aria-hidden="true">
            +
          </span>
        ) : (
          shown.map((person) => (
            <span
              key={person.id}
              className="ez-subs-avatar"
              title={person.name ?? person.email ?? 'Subscriber'}
              data-testid={`item-subscriber-${person.id}`}
              style={person.photoUrl ? { backgroundImage: `url(${person.photoUrl})` } : undefined}
            >
              {person.photoUrl ? '' : initials(person)}
            </span>
          ))
        )}
        {overflow > 0 ? (
          <span className="ez-subs-avatar ez-subs-avatar--more" data-testid="item-subscribers-more">
            +{overflow}
          </span>
        ) : null}
      </button>

      {open && anchor && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="ez-subs-popover"
              role="dialog"
              aria-label="Subscribers"
              data-testid="item-subscribers-popover"
              style={{ top: anchor.top, left: anchor.left }}
            >
              <button
                type="button"
                className={`ez-subs-watch${watching ? ' is-on' : ''}`}
                aria-pressed={watching}
                disabled={busy}
                data-testid="item-subscribers-watch"
                onClick={() => void toggleWatch()}
              >
                {watching ? 'Watching' : 'Watch'}
              </button>
              <h3 className="ez-subs-title">Subscribers</h3>
              <ul className="ez-subs-list">
                {subscribers.length === 0 ? (
                  <li className="ez-subs-empty">Nobody is subscribed to this item yet.</li>
                ) : (
                  subscribers.map((person) => (
                    <li key={person.id} className="ez-subs-row">
                      <span className="ez-subs-avatar ez-subs-avatar--small" aria-hidden="true">
                        {initials(person)}
                      </span>
                      <span className="ez-subs-name">{person.name ?? person.email ?? 'Unknown'}</span>
                      {person.title ? <span className="ez-subs-jobtitle">{person.title}</span> : null}
                      <button
                        type="button"
                        className="ez-subs-remove"
                        aria-label={`Remove ${person.name ?? 'subscriber'}`}
                        disabled={busy}
                        data-testid={`item-subscriber-remove-${person.id}`}
                        onClick={() => {
                          setBusy(true);
                          void source
                            .unsubscribe(itemId, [person.id])
                            .then(reload)
                            .finally(() => setBusy(false));
                        }}
                      >
                        ✕
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
